import { Button, Dialog } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { initialsFromTitle } from "./chatDisplay";
import {
  INVITATION_ACCEPT_LABEL,
  INVITATION_DECLINE_LABEL,
  INVITATION_DIALOG_TITLE,
} from "./standaloneChatCopy";

export type typeChatInvitationDialogRow = {
  chatInvitationId: string;
  title: string;
  invitedByDisplayName: string;
  memberCount: number;
};

/**
 * The real invitation preview dialog: chat name, initials plate, member
 * count, who invited them, and working Accept/Decline. `row` resolves from
 * the already-loaded invitation list in `messages.tsx` by matching
 * `search.chatInvitationId`.
 */
export function ChatInvitationDialog({
  open,
  onOpenChange,
  row,
  onAccept,
  onDecline,
  isBusy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: typeChatInvitationDialogRow | null;
  onAccept: () => void;
  onDecline: () => void;
  isBusy: boolean;
}): ReactNode {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={INVITATION_DIALOG_TITLE}
      autoFocus={false}
      footer={
        row === null ? null : (
          <>
            <Button variant="secondary" disabled={isBusy} onClick={onDecline}>
              {INVITATION_DECLINE_LABEL}
            </Button>
            <Button
              variant="primary"
              disabled={isBusy}
              loading={isBusy}
              onClick={onAccept}
            >
              {INVITATION_ACCEPT_LABEL}
            </Button>
          </>
        )
      }
    >
      {row === null ? null : (
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-solid text-h2 text-on-primary">
            {initialsFromTitle(row.title)}
          </span>
          <p className="text-body-strong text-ink">{row.title}</p>
          <p className="text-body text-ink-secondary">
            {row.invitedByDisplayName} invited you to {row.title}
          </p>
          <p className="text-caption text-ink-secondary">
            {row.memberCount} member{row.memberCount === 1 ? "" : "s"}
          </p>
        </div>
      )}
    </Dialog>
  );
}
